import clsx from "clsx";
import { FC, ReactNode } from "react";

import { PageListItem } from "./PageListItem";

import { TAB_QUERY_PARAM } from "components/constants";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { ActivityPageType, ActivityType, PageCategory, PageType } from "components/server-types";
import { Card } from "mds/components/Card";
import { DragAndDrop } from "mds/components/DragAndDrop";
import { TextAddButton } from "mds/components/TextAddButton";
import { useOrdered } from "mds/hooks/use-ordered";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  type ActivityWithPagesType,
  selectIsEditModeEnabled,
  selectPageById,
} from "store/selectors";
import { localStateActions } from "store/slices/local";
import { useDnDItemOrdering, useLocalCopy } from "utils/collections";
import { usePageActions } from "utils/page";
import { useParams } from "utils/routing";
import { CONTENT_SIDEBAR_TAB_NAME, pageUrl } from "utils/urls";

const PAGE_CATEGORY_TO_CONTENT_SIDEBAR_TAB_NAME: Record<PageCategory, CONTENT_SIDEBAR_TAB_NAME> = {
  activity_page: CONTENT_SIDEBAR_TAB_NAME.Content,
  assignment: CONTENT_SIDEBAR_TAB_NAME.Assignment,
  course_resource: CONTENT_SIDEBAR_TAB_NAME.CourseResource,
  instructor_workspace: CONTENT_SIDEBAR_TAB_NAME.InstructorWorkspace,
};

export type PagesCardProps = {
  pages: PageType[];
  header?: string | ReactNode;
  category?: PageCategory;
  activities?: ActivityType[];
  activityId?: string;
  alwaysShowDueDates?: boolean;
  canManagePages?: boolean;
  shouldLinkSidebar?: boolean;
  addTopicParam?: boolean;
  showCourseTitle?: boolean;
};

// TODO: Consider consolidating with activity card
// This is s card component for displaying pages with a header element above.
// It's configurable in that pages can be draggable and allow for adding new
// pages.
export const PagesCard: FC<PagesCardProps> = ({
  header,
  pages,
  category = "activity_page",
  activities,
  activityId,
  alwaysShowDueDates = false,
  showCourseTitle = false,
  canManagePages = false,
  shouldLinkSidebar,
  addTopicParam = false,
}) => {
  const dispatch = useAppDispatch();
  const pathParams = useParams();
  const [_pages, setPages] = useLocalCopy(pages);
  const pageId = pathParams.pageId;
  const selectedPage = useAppSelector((s) => selectPageById(s, pageId));
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);

  const orderedPages = useOrdered(_pages);
  const movePageLocally = useDnDItemOrdering(pages, setPages, "activity_id");
  const isActive = activityId && selectedPage?.activity_id === activityId;
  const { createPage, dropPage, navigateToPage } = usePageActions();

  const activity = activities?.find((a) => a.id === activityId);

  const getTopicId = (page: PageType) => {
    if (activity) {
      return activity.topic_id;
    }

    const pageActivity = activities?.find((a) => a.id === page.activity_id);
    return pageActivity?.topic_id;
  };

  const onPageClick = (page: PageType) => {
    const linkSidebar = shouldLinkSidebar
      ? PAGE_CATEGORY_TO_CONTENT_SIDEBAR_TAB_NAME[page.category]
      : undefined;
    navigateToPage(
      page,
      pageUrl(page.section_id, page.id, {
        [TAB_QUERY_PARAM]: linkSidebar,
        ...(addTopicParam ? { topicId: getTopicId(page) } : {}),
      }),
    );
    if (shouldLinkSidebar) {
      dispatch(localStateActions.setIsSidebarOpen(true));
    }
  };

  const createNewPage = () => {
    createPage(category, activityId);
  };

  const onDropPage = (droppedPage: ActivityPageType) =>
    dropPage(droppedPage, activities as ActivityWithPagesType[]);

  let headerElement = header ? (
    <div
      className={clsx("body-s mt-1 w-full truncate px-2 text-black-tint-40", {
        "cursor-pointer": isEditModeEnabled,
      })}
    >
      {header}
    </div>
  ) : null;

  if (canManagePages && header) {
    headerElement = (
      <DragAndDrop
        dragItemKind="page"
        dropContainerId={activityId || category}
        dropIndex={0}
        droppable={canManagePages && isEditModeEnabled}
        onDraggedItemHover={movePageLocally}
        onDropItem={onDropPage}
      >
        <HoverableToolbar reference="page" disableUnlessEditMode>
          <div
            className={clsx("body-s mt-1 w-full truncate px-2 text-black-tint-40", {
              "cursor-pointer": isEditModeEnabled,
            })}
          >
            {header}
          </div>
        </HoverableToolbar>
      </DragAndDrop>
    );
  }

  return (
    <>
      {headerElement}

      {orderedPages.length > 0 && (
        <Card className="mb-3 mt-2" isActive={isActive} small>
          {orderedPages?.map((page, index) => (
            <PageListItem
              alwaysShowDueDates={alwaysShowDueDates}
              containerId={activityId || category}
              key={page.id}
              page={page}
              pageIndex={index}
              showCourseTitle={showCourseTitle}
              topicId={getTopicId(page)}
              onDropPage={canManagePages && onDropPage}
              onMovePageLocally={canManagePages && movePageLocally}
              onPageClick={() => onPageClick(page)}
            />
          ))}

          {canManagePages && (
            <div className="h4 flex items-center justify-start">
              <TextAddButton
                reference="page"
                hideUnlessEditMode
                underlined
                onClick={createNewPage}
              />
            </div>
          )}
        </Card>
      )}
    </>
  );
};
